import { useId, useState, type JSX } from "react";
import type { LeadCardResponse, MergeLeadsResponse, PossibleDuplicate } from "@gioia365/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button } from "../../design/components/Button";
import { Icon } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { formatCount } from "../../i18n/format";
import { useLocale, useT } from "../../i18n/locale";
import { withLink } from "../../i18n/with-link";
import { leadScopeOf, useAccess } from "../../session/access";
import { tabKey, type TabContext } from "../../session/tab-context";
import { forgetLead } from "../archive/archive-queries";
import { useDismissPair } from "../cleanup/cleanup-queries";
import { MergePairDialog } from "../cleanup/MergePairDialog";
import { leadName } from "../lead-display";
import { leadKeys } from "../lead-queries";
import "./duplicate-notice.css";

// I possibili doppioni sotto la testata della scheda (LEAD-28): una riga per doppione, «Possibile doppione: stesso cellulare di
// {nome}» o «… stessa email di {nome}», con il link alla scheda dell'altro Lead; al più tre (il server ne manda tre), poi «e altri
// {n}», che porta a «Pulizia dati» chi la fa. «Unisci» apre la finestra dell'unione di «Pulizia dati» per quella coppia
// (`MergePairDialog`) e «Non sono doppioni» manda la stessa richiesta di «Pulizia dati», per chi può risolvere quella coppia
// (`canResolve`: la funzione e la modifica dei due Lead); per gli altri, sotto le righe, «Lo sistema chi fa la pulizia dei dati.».
// Dopo «Non sono doppioni» la scheda si rilegge e l'avviso conferma; dopo un'unione la finestra si chiude con l'avviso e la scheda si
// rilegge, oppure, se questo Lead è entrato nell'altro, si apre la scheda del Lead che resta. Scelta di Claude: un rifiuto di «Non
// sono doppioni» si legge nel riquadro, che resta.

type Props = {
  tab: TabContext;
  card: LeadCardResponse;
  view: string | null;
  timeZone: string;
};

export function DuplicateNotice({ tab, card, view, timeZone }: Props): JSX.Element | null {
  const t = useT();
  const { locale } = useLocale();
  const access = useAccess();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dismiss = useDismissPair(tab);
  const headingId = useId();
  const [merging, setMerging] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const { total, items } = card.possibleDuplicates;
  // La finestra dell'unione resta aperta anche se la scheda, riletta, non ha più doppioni: dice da sé che la coppia non c'è più.
  if (items.length === 0 && merging === null) return null;

  const leadId = card.lead.id;
  // «Pulizia dati» la apre chi la fa: come il pulsante dell'elenco, la funzione e la modifica di qualche Lead.
  const canClean = access.can("merge_duplicates") && leadScopeOf(access, "update") !== "none";
  const rest = total - items.length;

  async function notDuplicates(other: PossibleDuplicate) {
    if (dismiss.isPending) return;
    setProblem(null);
    try {
      await dismiss.mutateAsync({ leadIds: [leadId, other.leadId] });
      toast.show(t("dataCleanup.dismissed"), "success");
    } catch (error) {
      setProblem(messageForError(t, error));
    }
  }

  function merged(response: MergeLeadsResponse, text: string) {
    setMerging(null);
    toast.show(text, "success");
    // Questo Lead è entrato nell'altro: la sua scheda non c'è più, si va su quella del Lead che resta e soltanto dopo la si toglie.
    if (response.absorbedId === leadId) {
      void navigate({ to: "/lead/$leadId", params: { leadId: response.survivorId } }).then(() => forgetLead(queryClient, tab, response.absorbedId));
    } else forgetLead(queryClient, tab, response.absorbedId);
  }

  function dismissed(text: string) {
    setMerging(null);
    toast.show(text, "success");
  }

  const dialog =
    merging === null ? null : (
      <MergePairDialog
        tab={tab}
        view={view}
        timeZone={timeZone}
        canChangeOwner={access.can("change_owner")}
        leadId={leadId}
        otherLeadId={merging}
        onClose={() => setMerging(null)}
        onMerged={merged}
        onDismissed={dismissed}
        onGone={() => void queryClient.invalidateQueries({ queryKey: leadKeys.card(tabKey(tab), leadId, view) })}
      />
    );
  const notice =
    items.length === 0 ? null : (
      <section className="g-duplicate-notice" aria-labelledby={headingId}>
        <h2 id={headingId} className="g-visually-hidden">
          {t("leadCard.duplicates.label")}
        </h2>
        <ul className="g-duplicate-notice__list">
          {items.map((item) => {
            const name = leadName(item) ?? item.leadId;
            const text = t(item.reason === "phone" ? "leadCard.duplicates.samePhone" : "leadCard.duplicates.sameEmail");
            return (
              <li key={item.leadId} className="g-duplicate-notice__item">
                <Icon name="alert" />
                <span className="g-duplicate-notice__text">
                  {withLink(
                    text,
                    <Link to="/lead/$leadId" params={{ leadId: item.leadId }}>
                      {name}
                    </Link>,
                  )}
                </span>
                {item.canResolve ? (
                  <span className="g-duplicate-notice__actions">
                    <Button onClick={() => setMerging(item.leadId)} disabled={dismiss.isPending}>
                      {t("leadCard.duplicates.merge")}
                    </Button>
                    <Button variant="ghost" onClick={() => void notDuplicates(item)} disabled={dismiss.isPending} aria-busy={dismiss.isPending ? "true" : undefined}>
                      {t("leadCard.duplicates.notDuplicates")}
                    </Button>
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
        {rest > 0 ? (
          <p className="g-duplicate-notice__more">
            {canClean ? (
              <Link to="/lead" search={{ pulizia: "doppioni" }}>
                {t("leadCard.duplicates.more", { count: formatCount(locale, rest) })}
              </Link>
            ) : (
              t("leadCard.duplicates.more", { count: formatCount(locale, rest) })
            )}
          </p>
        ) : null}
        {items.some((item) => !item.canResolve) ? <p className="g-duplicate-notice__who">{t("leadCard.duplicates.readOnly")}</p> : null}
        {problem !== null ? (
          <Reason tone="warning" role="alert">
            {problem}
          </Reason>
        ) : null}
      </section>
    );

  // La finestra sta sempre nello stesso posto, dopo il riquadro: se il riquadro sparisce (la scheda riletta), non si rimonta.
  return (
    <>
      {notice}
      {dialog}
    </>
  );
}
